import {assert, assertAndContinue} from 'Asserts';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {assertNever} from 'util/Types';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsQuery = AnalyticsDashboard['analyticsQueries'][number];

export enum ScreenStateMode {
  BLOCK_EDITOR = 'BLOCK_EDITOR',
  DASHBOARD_EDITOR = 'DASHBOARD_EDITOR',
}

export interface DashboardEditorScreenState {
  /** If the query has been deleted from the dashboard, the id stored here might be invalid. */
  maybeValidExpandedAnalyticsQuery: Pick<AnalyticsQuery, 'id'> | null;
  screenStateMode: ScreenStateMode.DASHBOARD_EDITOR;
}

export enum BlockEditorPane {
  SQL_EDITOR = 'SQL_EDITOR',
  VISUALIZATION_EDITOR = 'VISUALIZATION_EDITOR',
  WAREHOUSE_BROWSER = 'WAREHOUSE_BROWSER',
}

export interface BlockEditorScreenState {
  analyticsQuery: Pick<AnalyticsQuery, 'id'>;
  expandedPane: BlockEditorPane | null;
  screenStateMode: ScreenStateMode.BLOCK_EDITOR;
}
export type ScreenState = DashboardEditorScreenState | BlockEditorScreenState;

export const DEFAULT_DASHBOARD_EDITOR_SCREEN_STATE: DashboardEditorScreenState = {
  maybeValidExpandedAnalyticsQuery: null,
  screenStateMode: ScreenStateMode.DASHBOARD_EDITOR,
};

export const analyticsDashboardGlobalScreenState = makeUseGlobalClientState<ScreenState>(
  DEFAULT_DASHBOARD_EDITOR_SCREEN_STATE
);

export function isBlockEditorScreenState(screenState: ScreenState): screenState is BlockEditorScreenState {
  return screenState.screenStateMode === ScreenStateMode.BLOCK_EDITOR;
}

export function assertBlockEditorScreenState(
  screenState: ScreenState
): asserts screenState is BlockEditorScreenState {
  assert(
    isBlockEditorScreenState(screenState),
    `Expected screenState type to be ${ScreenStateMode.BLOCK_EDITOR}`,
    {screenState}
  );
}

export function isDashboardEditorScreenState(
  screenState: ScreenState
): screenState is DashboardEditorScreenState {
  return screenState.screenStateMode === ScreenStateMode.DASHBOARD_EDITOR;
}

export function assertDashboardEditorScreenState(
  screenState: ScreenState
): asserts screenState is DashboardEditorScreenState {
  assert(
    isDashboardEditorScreenState(screenState),
    `Expected screenState type to be ${ScreenStateMode.DASHBOARD_EDITOR}`,
    {screenState}
  );
}

export function makeBlockEditorScreenState(analyticsQueryId: string): BlockEditorScreenState {
  return {
    analyticsQuery: {
      id: analyticsQueryId,
    },
    expandedPane: null,
    screenStateMode: ScreenStateMode.BLOCK_EDITOR,
  };
}

export function makeDashboardEditorScreenState(): DashboardEditorScreenState {
  return {...DEFAULT_DASHBOARD_EDITOR_SCREEN_STATE};
}

export function collapsePane(screenState: BlockEditorScreenState): BlockEditorScreenState {
  return {
    ...screenState,
    expandedPane: null,
  };
}

export function expandPane(
  screenState: BlockEditorScreenState,
  pane: BlockEditorPane
): BlockEditorScreenState {
  return {
    ...screenState,
    expandedPane: pane,
  };
}

export function collapseBlock(screenState: DashboardEditorScreenState): DashboardEditorScreenState {
  return {
    ...screenState,
    maybeValidExpandedAnalyticsQuery: null,
  };
}

export function expandBlock(
  screenState: DashboardEditorScreenState,
  analyticsQueryId: string
): DashboardEditorScreenState {
  return {
    ...screenState,
    maybeValidExpandedAnalyticsQuery: {
      id: analyticsQueryId,
    },
  };
}

export function getValidExpandedAnalyticsQueryId(
  analyticsDashboard: AnalyticsDashboard,
  screenState: DashboardEditorScreenState
): string | null {
  const analyticsQueryId = screenState.maybeValidExpandedAnalyticsQuery?.id;
  return analyticsQueryId != null &&
    analyticsDashboard.analyticsQueries.some((analyticsQuery) => analyticsQuery.id === analyticsQueryId)
    ? analyticsQueryId
    : null;
}

/**
 * Returns ids of blocks currently visible in a dashboard, ordered by layout top-to-bottom, left-to-right.
 */
export function getVisibleAnalyticsQueryIds(
  analyticsDashboard: AnalyticsDashboard,
  screenState: ScreenState
): Array<string> {
  const allAnalyticsQueryIds = analyticsDashboard.layoutRows.flatMap((layoutRow) =>
    layoutRow.analyticsQueries.map((analyticsQuery) => analyticsQuery.id)
  );
  switch (screenState.screenStateMode) {
    case ScreenStateMode.DASHBOARD_EDITOR: {
      const expandedAnalyticsQuery = getValidExpandedAnalyticsQueryId(analyticsDashboard, screenState);
      return expandedAnalyticsQuery != null ? [expandedAnalyticsQuery] : allAnalyticsQueryIds;
    }
    case ScreenStateMode.BLOCK_EDITOR:
      return [screenState.analyticsQuery.id];
    default:
      assertAndContinue(false, `Unexpected screen state: ${assertNever(screenState)}.`);
      return allAnalyticsQueryIds;
  }
}
